import {
  type ExperimentAssignmentState,
  useExperimentAssignment,
} from 'components/helpers/useExperimentAssignment';

export const AVAILABLE_SOON_NOTIFY_ME_FLAG = 'growth-26-09-available-soon-notify-me-cta';

// Variant serves GrowthBook booleans as 'true'; the localStorage QA override
// normalizes to 'on'.
const flagIsOn = (variant: string) => variant === 'true' || variant === 'on';

export type AvailableSoonNotifyMe = Pick<
  ExperimentAssignmentState,
  'isExperimentAssignment' | 'trackExposure'
> & {
  /** Whether the notify-me card may render. False until the assignment resolves. */
  enabled: boolean;
};

/**
 * The notify-me card's GrowthBook assignment. Reads through the experiment
 * path so experiment-backed serves can report exposure; a plain BFF flag read
 * records none, so an experiment gated on it would run blind. QA overrides
 * via localStorage `opendoor.cosmos.<flag>` still win.
 *
 * Takes `enabled` (the PDP is coming-soon) both as the experiment's eligibility
 * — an ineligible viewer is never bucketed, and the coming-soon state comes
 * from the listing, not the flag, so eligibility cannot bias the split — and
 * as the card gate: an ineligible PDP renders no card even once an
 * assignment resolved earlier in the session. Fails closed while a read is
 * unresolved; a resolved serve renders per the flag value, while only
 * experiment-backed serves enter the exposure stream.
 */
export const useAvailableSoonNotifyMe = (enabled: boolean): AvailableSoonNotifyMe => {
  const assignment = useExperimentAssignment({
    experimentKey: AVAILABLE_SOON_NOTIFY_ME_FLAG,
    enabled,
    honorLocalOverride: true,
  });

  return {
    enabled: enabled && assignment.variant != null && flagIsOn(assignment.variant),
    isExperimentAssignment: assignment.isExperimentAssignment,
    trackExposure: assignment.trackExposure,
  };
};
